import React, { useState } from 'react'
import { VStack, Text, Box, HStack, Flex, Icon } from '@chakra-ui/react'
import isEmpty from 'validator/lib/isEmpty'
import { addSkill } from '../../api/classification/classification'
import { useTranslation } from 'react-i18next'
import ModalInput from '../forms/modalForms/modalInput'
import TypeButton from '../buttons/typeButton'
import { BarChart012Icon, CheckCircleBrokenIcon, Target04Icon, WarningIcon } from '../../assets/icons'
import CustomSwitch, { BehaviorExamples } from '../forms/modalForms/customSwitch'
import FormModal from '../common/FormModal'
import { useFormMutation } from '../../hooks/useFormMutation'

interface AddSkillsModalProps {
  isOpen: boolean
  onClose: () => void
}

const AddSkillsModal: React.FC<AddSkillsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()

  const [behaviouralExamples, setBehaviouralExamples] = useState<BehaviorExamples>({
    foundational: [],
    operational: [],
    tactical: [],
    strategic: [],
  })

  const [skills, setSkills] = useState({
    skillName: '',
    description: '',
    contentType: 'skill',
  })

  const [errors, setErrors] = useState({
    skillName: false,
    description: false,
  })

  const resetForm = () => {
    setSkills({ skillName: '', description: '', contentType: 'skill' })
    setErrors({ skillName: false, description: false })
    setBehaviouralExamples({ foundational: [], operational: [], tactical: [], strategic: [] })
  }

  const createSkillMutation = useFormMutation({
    mutationFn: addSkill,
    queryKey: 'skills',
    successMessage: t('classifications.toasts.skillCreatedSuccessMessage'),
    onClose: () => {
      resetForm()
      onClose()
    },
  })

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setSkills((prev) => ({ ...prev, [name]: value }))
  }

  const handleRadioChange = (value: string | undefined) => {
    setSkills((prev) => ({ ...prev, contentType: value ?? prev.contentType }))
  }

  const isValid = () => {
    const newErrors = {
      skillName: isEmpty(skills.skillName.trim()),
      description: isEmpty(skills.description.trim()),
    }
    setErrors(newErrors)
    return !Object.values(newErrors).some((error) => error)
  }

  const handleSubmit = () => {
    if (!isValid()) return
    createSkillMutation.mutate({
      name: skills.skillName,
      description: skills.description,
      type: skills.contentType,
      behavioural_examples: {
        foundational: behaviouralExamples.foundational.filter(Boolean),
        operational: behaviouralExamples.operational.filter(Boolean),
        tactical: behaviouralExamples.tactical.filter(Boolean),
        strategic: behaviouralExamples.strategic.filter(Boolean),
      },
    })
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('classifications.actions.addNewSkillCompetenceTask')}
      onSubmit={handleSubmit}
      isLoading={createSkillMutation.isPending}
      size="lg"
    >
      <VStack align="left" spacing="4px" width="100%">
        <Text textStyle="l-xs" color="solid.500">
          {t('classifications.fields.contentType')}
        </Text>
        <HStack spacing={4}>
          <TypeButton
            label={t('content.types.skills')}
            icon={BarChart012Icon}
            contentType="skill"
            selectedType={skills.contentType}
            handleRadioChange={handleRadioChange}
          />
          <TypeButton
            label={t('content.types.competence')}
            icon={CheckCircleBrokenIcon}
            contentType="competence"
            selectedType={skills.contentType}
            handleRadioChange={handleRadioChange}
          />
          <TypeButton
            label={t('content.types.task')}
            icon={Target04Icon}
            contentType="task"
            selectedType={skills.contentType}
            handleRadioChange={handleRadioChange}
          />
        </HStack>
      </VStack>
      <Box width="100%">
        <ModalInput
          label={t('common.fields.name')}
          type="text"
          name="skillName"
          placeholder={t('classifications.placeholders.enterName')}
          value={skills.skillName}
          onChange={handleChange}
          isInvalid={!!errors.skillName}
        />
        {errors.skillName && (
          <Flex w="100%" mt={1} gap="4px">
            <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100">
              <WarningIcon />
            </Icon>
            <Text textStyle="b-xs" color="error.100">
              {t('classifications.validation.enterName')}
            </Text>
          </Flex>
        )}
      </Box>
      <Box width="100%">
        <ModalInput
          label={t('common.fields.description')}
          type="textarea"
          height="128px"
          name="description"
          placeholder={t('classifications.placeholders.addDescription')}
          value={skills.description}
          onChange={handleChange}
          isInvalid={!!errors.description}
        />
        {errors.description && (
          <Flex w="100%" mt={1} gap="4px">
            <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100">
              <WarningIcon />
            </Icon>
            <Text textStyle="b-xs" color="error.100">
              {t('classifications.validation.enterDescription')}
            </Text>
          </Flex>
        )}
      </Box>
      <Box width="100%">
        <Text textStyle="l-xs" color="solid.500" ml={1} mb={2}>
          {t('classifications.sections.behaviouralExamples')}
        </Text>
        <CustomSwitch
          type={skills.contentType as 'skill' | 'competence' | 'task'}
          initialState={0}
          onToggle={() => {}}
          onExamplesChange={(examples) => setBehaviouralExamples(examples)}
        />
      </Box>
    </FormModal>
  )
}

export default AddSkillsModal
